import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import useFavorites from "@/hooks/useFavorites";
import { useToast } from "@/components/feature/ToastProvider";
import Breadcrumb from "@/components/base/Breadcrumb";
import ShareDialog from "@/components/base/ShareDialog";
import EmptyState from "@/components/base/EmptyState";
import DetailBanner from "./components/DetailBanner";
import RelatedList from "./components/RelatedList";
import PlotChapterNav from "./components/PlotChapterNav";
import { allContent, findContentById, showInfo } from "@/mocks/content";
import type { ContentItem } from "@/types/content";

const typeBadge: Record<ContentItem["type"], string> = {
  plot: "剧情介绍",
  cast: "演员信息",
  review: "精彩剧评",
  news: "最新动态",
};

function getDisplayTitle(item: ContentItem) {
  return item.type === "cast" ? `${item.actor} 饰 ${item.roleName}` : item.title;
}

export default function Detail() {
  const { id = "" } = useParams();
  const item = findContentById(id);
  const { toggle, isFavorite } = useFavorites();
  const { showToast } = useToast();
  const [shareOpen, setShareOpen] = useState(false);

  const displayTitle = item ? getDisplayTitle(item) : "内容不存在";

  useDocumentMeta({
    title: item
      ? `${displayTitle} - 我的朋友他的妻子`
      : "内容不存在 - 我的朋友他的妻子",
    description: item ? item.summary : "该内容已下线或不存在。",
    keywords: item
      ? `${displayTitle},我的朋友他的妻子,${typeBadge[item.type]}`
      : "我的朋友他的妻子",
  });

  if (!item) {
    return (
      <div className="mx-auto w-full max-w-content px-4 py-20 md:px-6">
        <EmptyState message="该内容已下线或不存在" icon="ri-error-warning-line" />
        <div className="mt-6 flex justify-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const favorited = isFavorite(item.id);
  const cover = item.cover;

  const metas: { icon: string; text: string }[] = [];
  if (item.type === "plot") {
    metas.push({ icon: "ri-bookmark-3-line", text: item.chapter });
    metas.push({ icon: "ri-calendar-line", text: `更新于 ${item.updatedAt}` });
  } else if (item.type === "cast") {
    metas.push({ icon: "ri-user-star-line", text: `${item.category} · ${item.gender}` });
    metas.push({ icon: "ri-calendar-line", text: `资料更新 ${item.updatedAt}` });
  } else if (item.type === "review") {
    metas.push({ icon: "ri-user-line", text: item.author });
    metas.push({ icon: "ri-calendar-line", text: item.date });
    metas.push({ icon: "ri-fire-line", text: `热度 ${item.popularity}` });
  } else {
    metas.push({ icon: "ri-calendar-line", text: item.date });
  }

  const related = allContent
    .filter((entry) => entry.type === item.type && entry.id !== item.id)
    .slice(0, 4);

  const tags = item.type === "plot" || item.type === "review" ? item.tags : [];

  const handleFavorite = () => {
    const nowFavorited = toggle(item.id);
    showToast(nowFavorited ? "已加入收藏，可在本机继续查看" : "已取消收藏");
  };

  const paragraphs = item.type === "cast" ? item.bio : item.content;

  return (
    <>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-content px-4 py-4 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: typeBadge[item.type], to: `/category/${item.type === "cast" ? "cast" : item.type === "review" ? "reviews" : item.type === "news" ? "news" : "plot"}` },
              { label: displayTitle },
            ]}
          />
        </div>
      </div>

      <DetailBanner
        cover={cover}
        badge={typeBadge[item.type]}
        title={displayTitle}
        metas={metas}
      />

      <div className="mx-auto w-full max-w-content px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3 lg:gap-8">
          <article className="lg:col-span-2">
            {item.type === "cast" && (
              <div className="mb-8 flex flex-col gap-4 rounded-lg border border-background-300 bg-background-50 p-5 sm:flex-row sm:items-center">
                <div className="h-24 w-24 shrink-0 overflow-hidden rounded-full">
                  <img
                    src={item.avatar}
                    alt={item.actor}
                    title={`${item.actor} 饰 ${item.roleName}`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground-950">{item.actor}</h3>
                  <p className="mt-0.5 text-sm text-primary-600">
                    饰演角色：{item.roleName}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    {item.intro}
                  </p>
                </div>
              </div>
            )}

            {item.type === "cast" && (
              <section className="mb-8 rounded-lg bg-secondary-50 p-5">
                <h2 className="mb-3 flex items-center gap-2 text-base font-bold text-foreground-950">
                  <i className="ri-user-heart-line text-primary-500"></i>
                  角色简介 · {item.roleName}
                </h2>
                <p className="text-sm leading-relaxed text-foreground-700">
                  {item.character}
                </p>
              </section>
            )}

            <div className="flex flex-col gap-5">
              <h2 className="text-lg font-bold text-foreground-950 md:text-xl">
                {item.type === "cast" ? "演员简介" : "正文内容"}
              </h2>
              {paragraphs.map((paragraph, index) => (
                <p
                  key={index}
                  className="text-sm leading-7 text-foreground-700 md:text-[15px] md:leading-8"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            {tags.length > 0 && (
              <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-5">
                <span className="text-sm text-foreground-500">相关标签：</span>
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-900"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </article>

          <aside className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-300 bg-background-50 p-5">
              <h3 className="text-sm font-semibold text-foreground-950">内容操作</h3>
              <div className="mt-4 flex flex-col gap-3">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
                    favorited
                      ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                      : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
                  {favorited ? "已收藏" : "收藏本页"}
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className="ri-share-forward-line"></i>
                  分享内容
                </button>
              </div>
              <p className="mt-3 text-xs text-foreground-400">
                收藏保存在本机浏览器 · 分享为演示功能
              </p>
            </div>

            {item.type === "plot" && (
              <div className="rounded-lg border border-background-300 bg-background-50 p-5">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground-950">
                  <i className="ri-list-check-2 text-primary-500"></i>
                  章节导航
                </h3>
                <PlotChapterNav currentId={item.id} />
              </div>
            )}

            <div className="rounded-lg border border-background-300 bg-background-50 p-5">
              <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-foreground-950">
                <i className="ri-links-line text-primary-500"></i>
                {typeBadge[item.type]}推荐
              </h3>
              <RelatedList items={related} />
            </div>

            <div className="rounded-lg bg-primary-900 p-5 text-background-50">
              <h3 className="text-sm font-semibold">关于《{showInfo.name}》</h3>
              <p className="mt-2 text-xs leading-relaxed text-primary-100">
                {showInfo.tagline} · {showInfo.genre}
              </p>
              <div className="mt-4 flex flex-wrap gap-2 text-xs text-primary-100">
                <span className="rounded-full bg-primary-800 px-2.5 py-1">
                  评分 {showInfo.rating}
                </span>
                <span className="rounded-full bg-primary-800 px-2.5 py-1">
                  {showInfo.episodes}
                </span>
                <span className="rounded-full bg-primary-800 px-2.5 py-1">
                  {showInfo.year}
                </span>
              </div>
              <Link
                to="/category/plot"
                className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-accent-300 hover:text-accent-200"
              >
                查看完整剧情
                <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>
          </aside>
        </div>
      </div>

      <ShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title={displayTitle}
      />
    </>
  );
}